import {
  Button,
  Card,
  Empty,
  Input,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Upload,
} from 'antd';
import { DownloadOutlined, PlusOutlined, SearchOutlined, TeamOutlined, UploadOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import { useExcelMigrationIo } from '@/hooks/useExcelMigrationIo';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type StudentRow = {
  id: string;
  admission_no: string;
  first_name: string;
  last_name: string | null;
  status: string;
  campus_id?: string | null;
  campus_code?: string | null;
  campus_name?: string | null;
  gender?: string | null;
  category?: string | null;
  religion?: string | null;
  quota_id?: string | null;
  quota_code?: string | null;
  quota_name?: string | null;
  pen_id?: string | null;
  apaar_id?: string | null;
  admission_date?: string | null;
  admission_type?: string | null;
  guardian_count?: number;
  session_name?: string | null;
  grade_name?: string | null;
  section_name?: string | null;
  enrollment_status?: string | null;
};

type StudentStats = {
  total: number;
  active: number;
  without_placement: number;
  without_guardian: number;
  by_gender: Array<{ label: string; count: number }>;
  by_category: Array<{ label: string; count: number }>;
  by_religion: Array<{ label: string; count: number }>;
  by_quota: Array<{ label: string; count: number; quota_id: string | null }>;
};

type StaffRow = {
  id: string;
  employee_id: string;
  user_id: string | null;
  employee_code?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  campus_code?: string | null;
  campus_name?: string | null;
  is_active?: number | boolean;
  student_assignment_count?: number;
  section_assignment_count?: number;
};

type Campus = { id: string; code: string; name: string };

const STUDENT_STATUSES = ['active', 'inactive', 'withdrawn', 'alumni', 'transferred_out'];

function statusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'green';
    case 'inactive':
      return 'default';
    case 'withdrawn':
      return 'orange';
    case 'alumni':
      return 'blue';
    case 'transferred_out':
      return 'purple';
    default:
      return 'default';
  }
}

function displayName(first?: string | null, last?: string | null) {
  return [first, last].filter(Boolean).join(' ') || '—';
}

function placementLabel(row: StudentRow) {
  const grade = row.grade_name || '';
  const section = row.section_name || '';
  if (!grade && !section) return '—';
  return section ? `${grade}–${section}` : grade;
}

export default function StudentListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const view = params.get('view');
  const isTeachers = view === 'teachers';
  const user = useAuthStore((state) => state.user);
  const canManage = Boolean(user?.permissions?.includes('education.student.manage'));
  const canStaff = Boolean(
    user?.permissions?.includes('education.staff.manage') ||
      user?.permissions?.includes('education.student.view_all') ||
      user?.permissions?.includes('education.student.manage')
  );

  const q = params.get('q') || '';
  const status = params.get('status') || undefined;
  const campusId = params.get('campus') || undefined;
  const gradeFilter = params.get('grade') || undefined;
  const gender = params.get('gender') || undefined;
  const category = params.get('category') || undefined;
  const religion = params.get('religion') || undefined;
  const quotaId = params.get('quota') || undefined;
  const [draftQ, setDraftQ] = useState(q);

  const listParams = {
    q: q || undefined,
    status,
    campus_id: campusId,
    gender,
    category,
    religion,
    quota_id: quotaId,
  };

  const { isExcelBusy, downloadTemplate, downloadExport, handleImport } = useExcelMigrationIo({
    templateUrl: '/education/students/excel/template',
    exportUrl: '/education/students/excel/export',
    importUrl: '/education/students/excel/import',
    templateFilename: 'education-students-import-template.xlsx',
    exportFilenamePrefix: 'education-students',
    onImportSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education-students'] });
      queryClient.invalidateQueries({ queryKey: ['education-students-stats'] });
    },
    buildExportParams: () => listParams,
  });

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const toggleStatFilter = (key: string, value?: string) => {
    const current = params.get(key) || undefined;
    setFilter(key, current === value ? undefined : value);
  };

  const campuses = useQuery({
    queryKey: ['education-campuses'],
    queryFn: () => api.get<Campus[]>('/education/campuses'),
    enabled: !isTeachers,
  });

  const studentsQuery = useQuery({
    queryKey: ['education-students', listParams],
    queryFn: () => api.get<StudentRow[]>('/education/students', { params: listParams }),
    enabled: !isTeachers,
  });

  const statsQuery = useQuery({
    queryKey: ['education-students-stats', q, status, campusId],
    queryFn: () =>
      api.get<StudentStats>('/education/students/stats', {
        params: { q: q || undefined, status, campus_id: campusId },
      }),
    enabled: !isTeachers,
  });

  const staffQuery = useQuery({
    queryKey: ['education-staff'],
    queryFn: () => api.get<StaffRow[]>('/education/staff'),
    enabled: isTeachers && canStaff,
  });

  const studentRows = useMemo(() => {
    const all = studentsQuery.data?.data ?? [];
    if (!gradeFilter) return all;
    const needle = gradeFilter.trim().toLowerCase();
    return all.filter((row) => {
      const label = `${row.grade_name || ''} ${row.section_name || ''}`.toLowerCase();
      return label.includes(needle);
    });
  }, [studentsQuery.data?.data, gradeFilter]);

  const board = statsQuery.data?.data;
  const stats = {
    total: board?.total ?? studentRows.length,
    active: board?.active ?? 0,
    withoutPlacement: board?.without_placement ?? 0,
    withoutGuardian: board?.without_guardian ?? 0,
  };

  if (isTeachers) {
    const staffRows = staffQuery.data?.data ?? [];
    return (
      <EducationPageChrome
        title={t('teachers')}
        description={t('teachersRegisterIntro')}
        tags={<Tag icon={<TeamOutlined />}>{staffRows.length}</Tag>}
        extra={
          <Button onClick={() => navigate('/education/students')}>{t('openStudents')}</Button>
        }
      >
        <Card size="small">
          <Table
            rowKey="id"
            loading={staffQuery.isLoading}
            dataSource={staffRows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('teachersEmpty')} />
              ),
            }}
            columns={[
              {
                title: t('name'),
                render: (_v, row) => displayName(row.first_name, row.last_name),
              },
              { title: t('employeeCode'), dataIndex: 'employee_code', width: 140 },
              { title: t('employeeId'), dataIndex: 'employee_id', ellipsis: true, width: 160 },
              {
                title: t('campus'),
                render: (_v, row) =>
                  row.campus_code ? `${row.campus_code} — ${row.campus_name || ''}` : '—',
              },
              {
                title: t('studentAssignments'),
                dataIndex: 'student_assignment_count',
                width: 120,
                align: 'right',
              },
              {
                title: t('sectionAssignments'),
                dataIndex: 'section_assignment_count',
                width: 120,
                align: 'right',
              },
              {
                title: t('status'),
                width: 100,
                render: (_v, row) => (
                  <Tag color={row.is_active ? 'green' : 'default'}>
                    {row.is_active ? t('active') : t('inactive')}
                  </Tag>
                ),
              },
            ]}
          />
        </Card>
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('students')}
      description={t('studentsRegisterIntro')}
      tags={
        <>
          <Tag>{stats.total} {t('students')}</Tag>
          <Tag color="green">{stats.active} {t('active')}</Tag>
        </>
      }
      extra={
        <Space wrap>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={isExcelBusy}>
            {t('downloadTemplate')}
          </Button>
          <Button icon={<DownloadOutlined />} onClick={downloadExport} disabled={isExcelBusy}>
            {t('exportExcel')}
          </Button>
          {canManage ? (
            <Upload accept=".xlsx,.xls" showUploadList={false} customRequest={handleImport} disabled={isExcelBusy}>
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                {t('importExcel')}
              </Button>
            </Upload>
          ) : null}
          {canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/education/students/new')}>
              {t('newStudent')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={statsQuery.isLoading}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap size="large">
              <div
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setFilter('gender', undefined);
                  setFilter('category', undefined);
                  setFilter('religion', undefined);
                  setFilter('quota', undefined);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setFilter('gender', undefined);
                    setFilter('category', undefined);
                    setFilter('religion', undefined);
                    setFilter('quota', undefined);
                  }
                }}
              >
                <Statistic title={t('statTotal')} value={stats.total} />
              </div>
              <Statistic title={t('statActive')} value={stats.active} />
              <Statistic title={t('statNoPlacement')} value={stats.withoutPlacement} />
              <Statistic title={t('statNoGuardian')} value={stats.withoutGuardian} />
            </Space>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('genderWise', { defaultValue: 'Gender-wise' })}</div>
              <Space wrap>
                {(board?.by_gender ?? []).map((item) => (
                  <Tag
                    key={`g-${item.label}`}
                    color={gender === item.label ? 'blue' : undefined}
                    style={{ cursor: 'pointer', padding: '4px 10px' }}
                    onClick={() => toggleStatFilter('gender', item.label)}
                  >
                    {item.label}: {item.count}
                  </Tag>
                ))}
              </Space>
            </div>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('categoryWise', { defaultValue: 'Category-wise' })}</div>
              <Space wrap>
                {(board?.by_category ?? []).map((item) => (
                  <Tag
                    key={`c-${item.label}`}
                    color={category === item.label ? 'blue' : undefined}
                    style={{ cursor: 'pointer', padding: '4px 10px' }}
                    onClick={() => toggleStatFilter('category', item.label)}
                  >
                    {item.label}: {item.count}
                  </Tag>
                ))}
              </Space>
            </div>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('quotaWise', { defaultValue: 'Quota-wise' })}</div>
              <Space wrap>
                {(board?.by_quota ?? []).map((item) => (
                  <Tag
                    key={`q-${item.quota_id || item.label}`}
                    color={quotaId === (item.quota_id || '__none__') ? 'blue' : undefined}
                    style={{ cursor: 'pointer', padding: '4px 10px' }}
                    onClick={() => toggleStatFilter('quota', item.quota_id || '__none__')}
                  >
                    {item.label}: {item.count}
                  </Tag>
                ))}
              </Space>
            </div>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('religionWise', { defaultValue: 'Religion-wise' })}</div>
              <Space wrap>
                {(board?.by_religion ?? []).map((item) => (
                  <Tag
                    key={`r-${item.label}`}
                    color={religion === item.label ? 'blue' : undefined}
                    style={{ cursor: 'pointer', padding: '4px 10px' }}
                    onClick={() => toggleStatFilter('religion', item.label)}
                  >
                    {item.label}: {item.count}
                  </Tag>
                ))}
              </Space>
            </div>
            {(gender || category || religion || quotaId) && (
              <Button
                size="small"
                onClick={() => {
                  setFilter('gender', undefined);
                  setFilter('category', undefined);
                  setFilter('religion', undefined);
                  setFilter('quota', undefined);
                }}
              >
                {t('clearStatFilters', { defaultValue: 'Clear demographic filters' })}
              </Button>
            )}
          </Space>
        </Card>

        <Card size="small">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <Input.Search
                allowClear
                prefix={<SearchOutlined />}
                placeholder={t('studentSearchPlaceholder')}
                value={draftQ}
                onChange={(e) => setDraftQ(e.target.value)}
                onSearch={(value) => setFilter('q', value.trim() || undefined)}
                style={{ width: 280 }}
              />
              <Select
                allowClear
                placeholder={t('status')}
                value={status}
                style={{ minWidth: 160 }}
                options={STUDENT_STATUSES.map((item) => ({ value: item, label: t(`studentStatus.${item}`, { defaultValue: item }) }))}
                onChange={(value) => setFilter('status', value)}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('campus')}
                value={campusId}
                style={{ minWidth: 220 }}
                loading={campuses.isLoading}
                options={(campuses.data?.data ?? []).map((c) => ({
                  value: c.id,
                  label: `${c.code} — ${c.name}`,
                }))}
                onChange={(value) => setFilter('campus', value)}
              />
              <Input
                allowClear
                placeholder={t('classSectionFilter')}
                value={gradeFilter}
                onChange={(e) => setFilter('grade', e.target.value || undefined)}
                style={{ width: 180 }}
              />
            </Space>

            <Table
              rowKey="id"
              loading={studentsQuery.isLoading}
              dataSource={studentRows}
              scroll={{ x: 1100 }}
              pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (n) => `${n}` }}
              locale={{
                emptyText: (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('studentsEmpty')}>
                    {canManage ? (
                      <Button type="primary" onClick={() => navigate('/education/students/new')}>
                        {t('newStudent')}
                      </Button>
                    ) : null}
                  </Empty>
                ),
              }}
              onRow={(row) => ({
                onClick: () => navigate(`/education/students/${row.id}`),
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: t('admissionNo'), dataIndex: 'admission_no', width: 140, fixed: 'left' },
                {
                  title: t('name'),
                  width: 180,
                  fixed: 'left',
                  render: (_v, row) => displayName(row.first_name, row.last_name),
                },
                {
                  title: t('campus'),
                  width: 140,
                  render: (_v, row) => row.campus_code || row.campus_name || '—',
                },
                {
                  title: t('classSection'),
                  width: 140,
                  render: (_v, row) => placementLabel(row),
                },
                {
                  title: t('session'),
                  dataIndex: 'session_name',
                  width: 140,
                  render: (value: string | null) => value || '—',
                },
                {
                  title: t('gender'),
                  dataIndex: 'gender',
                  width: 100,
                  render: (value: string | null) => value || '—',
                },
                {
                  title: t('category'),
                  dataIndex: 'category',
                  width: 100,
                  render: (value: string | null) => value || '—',
                },
                {
                  title: t('quota', { defaultValue: 'Quota' }),
                  width: 120,
                  render: (_v, row) => row.quota_code || '—',
                },
                {
                  title: t('religion', { defaultValue: 'Religion' }),
                  dataIndex: 'religion',
                  width: 110,
                  render: (value: string | null) => value || '—',
                },
                {
                  title: t('penId'),
                  dataIndex: 'pen_id',
                  width: 120,
                  render: (value: string | null) => value || '—',
                },
                {
                  title: t('guardians'),
                  dataIndex: 'guardian_count',
                  width: 100,
                  align: 'right',
                  render: (value: number) => value ?? 0,
                },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 130,
                  fixed: 'right',
                  render: (value: string) => <Tag color={statusColor(value)}>{value}</Tag>,
                },
              ]}
            />
          </Space>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
